import {
  Badge,
  Button,
  buttonClass,
  Checkbox,
  EmptyState,
  Notice,
  Panel,
  SegmentedControl,
  Switch,
} from '@mogzo/ui';
import { Clock, Ellipsis, Gauge, Plus, Trash2, Upload, X } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { type CopyPicks, CopyToLive, copiedText } from '../components/CopyToLive';
import { EventForm } from '../components/EventForm';
import { CliTag, touchedByCli } from '../components/MadeFrom';
import { useProject } from '../components/ProjectLayout';
import { RuleForm } from '../components/RuleForm';
import { ScheduleTimeline, TimelineKey } from '../components/ScheduleTimeline';
import { SelectionBar } from '../components/SelectionBar';
import { ErrorNotice, Loading, TextLink } from '../components/ui';
import { ApiError, apiSend } from '../lib/api';
import {
  at,
  atDay,
  countOf,
  ruleDoes,
  ruleWhen,
  type Schedule,
  type ScheduleEvent,
  type ScheduleRule,
  shown,
  whenOf,
} from '../lib/schedule';
import { useApi } from '../lib/use-api';

type Env = 'test' | 'prod';

const STATUS: Record<
  ScheduleEvent['status'],
  { tone: 'success' | 'info' | 'neutral' | 'outline'; label: string; dot: boolean }
> = {
  on: { tone: 'success', label: 'On now', dot: true },
  scheduled: { tone: 'info', label: 'Scheduled', dot: true },
  ended: { tone: 'neutral', label: 'Ended', dot: false },
  off: { tone: 'outline', label: 'Turned off', dot: false },
};

function failure(error: unknown): string {
  return error instanceof ApiError ? error.message : 'Could not reach the API. Try again.';
}

function nextOf(event: ScheduleEvent): string {
  if (event.run) return `Ends ${at(event.run.end)}`;
  if (event.status === 'scheduled' && event.next) return `Starts ${at(event.next.start)}`;
  return '—';
}

/** What a run in Recent runs came from, in words. */
function fromOf(kind: string, sourceId: string, schedule: Schedule): string {
  if (kind !== 'rule') return 'Event';
  const rule = schedule.rules.find((r) => String(r.id) === sourceId);
  return rule ? `Rule · ${ruleWhen(rule, schedule).main}` : 'A rule since deleted';
}

/**
 * The Schedule tab (M37, the approved mockup): a game's live events and rules, test and live,
 * the next two weeks of them, and what they did. Live is changed here directly, like remote
 * config; test copies to live.
 */
export function ProjectSchedule() {
  const { project } = useProject();
  const [params, setParams] = useSearchParams();
  const env: Env = params.get('env') === 'prod' ? 'prod' : 'test';
  const editingEvent = params.get('event');
  const editingRule = params.get('rule');
  const [reload, setReload] = useState(0);
  const schedule = useApi<Schedule>(`/v1/dev/projects/${project.id}/schedule?env=${env}`, reload);
  // Copy to live: closed (false), or open with these ticked (null: what's new or changed).
  const [copying, setCopying] = useState<CopyPicks | null | false>(false);
  const [message, setMessage] = useState<{
    tone: 'success' | 'danger' | 'warning';
    text: string;
  } | null>(null);
  const [busyRule, setBusyRule] = useState<number | null>(null);
  const [picked, setPicked] = useState<CopyPicks>({ events: [], rules: [] });
  const [bulkBusy, setBulkBusy] = useState(false);
  // What just went to Trash, for Undo.
  const [moved, setMoved] = useState<{ groups: string[]; text: string; many: boolean } | null>(
    null,
  );
  // Picks belong to one schedule.
  // biome-ignore lint/correctness/useExhaustiveDependencies: clears them when the schedule changes
  useEffect(() => setPicked({ events: [], rules: [] }), [env]);

  const go = (next: { env?: Env; event?: string | null; rule?: string | null }) => {
    const query = new URLSearchParams();
    if ((next.env ?? env) === 'prod') query.set('env', 'prod');
    if (next.event) query.set('event', next.event);
    if (next.rule) query.set('rule', next.rule);
    setParams(query);
  };
  const refresh = () => setReload((n) => n + 1);

  const trashed = (groups: string[], text: string, many = false) => {
    setMessage(null);
    setMoved({ groups, text, many });
    refresh();
  };

  const isPicked = {
    event: (id: string) => picked.events.includes(id),
    rule: (id: number) => picked.rules.includes(id),
  };
  const pick = {
    event: (id: string) =>
      setPicked((now) => ({
        ...now,
        events: now.events.includes(id) ? now.events.filter((e) => e !== id) : [...now.events, id],
      })),
    rule: (id: number) =>
      setPicked((now) => ({
        ...now,
        rules: now.rules.includes(id) ? now.rules.filter((r) => r !== id) : [...now.rules, id],
      })),
  };

  async function bulk(action: 'on' | 'off' | 'trash') {
    setBulkBusy(true);
    try {
      const result = await apiSend<{ changed: number; groups: string[] }>(
        'POST',
        `/v1/dev/projects/${project.id}/schedule/bulk?env=${env}`,
        { action, ...picked },
      );
      const what = countOf(picked.events.length, picked.rules.length);
      setPicked({ events: [], rules: [] });
      if (action === 'trash') {
        trashed(result.groups, `Moved ${what} to Trash`, result.changed > 1);
      } else {
        setMoved(null);
        setMessage({ tone: 'success', text: `Turned ${action} ${what}.` });
        refresh();
      }
    } catch (error) {
      setMessage({ tone: 'danger', text: failure(error) });
    } finally {
      setBulkBusy(false);
    }
  }

  async function undo() {
    if (!moved) return;
    try {
      const result = await apiSend<{ failed: { message: string }[] }>(
        'POST',
        `/v1/dev/projects/${project.id}/schedule/trash/restore`,
        { groups: moved.groups },
      );
      setMoved(null);
      if (result.failed.length) {
        setMessage({ tone: 'danger', text: result.failed.map((f) => f.message).join(' ') });
      }
      refresh();
    } catch (error) {
      setMessage({ tone: 'danger', text: failure(error) });
    }
  }

  async function toggle(rule: ScheduleRule, enabled: boolean) {
    setBusyRule(rule.id);
    try {
      const { id: _id, last: _l, next: _n, origin: _o, ...settings } = rule;
      await apiSend('PUT', `/v1/dev/projects/${project.id}/rules/${rule.id}?env=${env}`, {
        ...settings,
        enabled,
      });
      refresh();
    } catch (error) {
      setMessage({ tone: 'danger', text: failure(error) });
    } finally {
      setBusyRule(null);
    }
  }

  async function runNow(rule: ScheduleRule) {
    setBusyRule(rule.id);
    try {
      const result = await apiSend<{ status: 'done' | 'failed'; message: string }>(
        'POST',
        `/v1/dev/projects/${project.id}/rules/${rule.id}/run`,
      );
      setMessage({ tone: result.status === 'done' ? 'success' : 'danger', text: result.message });
      refresh();
    } catch (error) {
      setMessage({ tone: 'danger', text: failure(error) });
    } finally {
      setBusyRule(null);
    }
  }

  const trashCount = schedule.status === 'ready' ? schedule.data.trash : 0;
  const header = (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
      <SegmentedControl
        label="Which schedule"
        value={env}
        onChange={(next) => go({ env: next })}
        items={[
          { id: 'test', label: 'Test' },
          { id: 'prod', label: 'Live' },
        ]}
      />
      <p className="m-0 flex-[1_1_320px] text-[13px] text-muted">
        {env === 'test'
          ? 'What your test community plays with. Try events and rules here, then copy them to live.'
          : 'What your published game’s players get, at the times you set. No new version needed.'}
      </p>
      <Badge tone={env === 'test' ? 'info' : 'success'}>
        {env === 'test' ? 'Test schedule' : 'Live schedule'}
      </Badge>
      <Link to={`/projects/${project.id}/schedule/trash`} className={buttonClass()}>
        <Trash2 size={16} strokeWidth={1.75} aria-hidden="true" />
        <span>Trash</span>
        {trashCount > 0 && <span className="mz-badge">{trashCount}</span>}
      </Link>
    </div>
  );

  if (schedule.status === 'loading')
    return (
      <>
        {header}
        <Loading />
      </>
    );
  if (schedule.status === 'error')
    return (
      <>
        {header}
        <ErrorNotice error={schedule.error} />
      </>
    );
  const data = schedule.data;

  if (editingEvent) {
    const event = data.events.find((e) => e.id === editingEvent) ?? null;
    return (
      <>
        {header}
        <EventForm
          key={`${env}:${editingEvent}`}
          project={project}
          env={env}
          schedule={data}
          event={editingEvent === 'new' ? null : event}
          onDone={(id) => {
            refresh();
            go({ event: id });
          }}
          onTrashed={(group, name) => {
            trashed([group], `Moved ${name} to Trash`);
            go({});
          }}
          onCancel={() => go({})}
        />
      </>
    );
  }
  if (editingRule) {
    const rule = data.rules.find((r) => String(r.id) === editingRule) ?? null;
    return (
      <>
        {header}
        <RuleForm
          key={`${env}:${editingRule}`}
          project={project}
          env={env}
          schedule={data}
          rule={editingRule === 'new' ? null : rule}
          onDone={() => {
            refresh();
            go({});
          }}
          onTrashed={(group) => {
            trashed([group], 'Moved the rule to Trash');
            go({});
          }}
          onCancel={() => go({})}
        />
      </>
    );
  }

  const onNow = data.events.filter((e) => e.status === 'on');
  const soonest = data.events
    .filter((e) => e.status === 'scheduled' && e.next)
    .sort((a, b) => (a.next?.start ?? '').localeCompare(b.next?.start ?? ''))[0];

  const allEvents = data.events.length > 0 && data.events.every((e) => isPicked.event(e.id));
  const allRules = data.rules.length > 0 && data.rules.every((r) => isPicked.rule(r.id));
  const pickedCount = picked.events.length + picked.rules.length;

  return (
    <>
      {header}
      {moved && (
        <Notice
          tone="success"
          title={moved.text}
          action={
            <span className="flex flex-wrap gap-2">
              <Button size="sm" onClick={undo}>
                Undo
              </Button>
              <Link
                to={`/projects/${project.id}/schedule/trash`}
                className={buttonClass('ghost', 'sm')}
              >
                Open Trash
              </Link>
            </span>
          }
        >
          {moved.many
            ? 'They stop running now. Trash keeps them 30 days, with any leaderboard scores.'
            : 'It stops running now. Trash keeps it 30 days, with any leaderboard scores.'}
        </Notice>
      )}
      {message && (
        <Notice
          tone={message.tone}
          action={
            <Button size="sm" variant="ghost" onClick={() => setMessage(null)}>
              Close
            </Button>
          }
        >
          {message.text}
        </Notice>
      )}
      {onNow.map((event) => {
        const [key, value] = Object.entries(event.config)[0] ?? [];
        return (
          <Notice
            key={event.id}
            tone="success"
            title={`${event.name} is on now${event.run?.test ? ' for testers' : ''}`}
          >
            Until {atDay(event.run?.end ?? '')} UTC.
            {key !== undefined && (
              <>
                {' '}
                {env === 'test' ? 'Testers' : 'Players'} get <code className="mz-code">{key}</code>{' '}
                {shown(value)}
                {key in data.config ? ` instead of ${shown(data.config[key])}` : ''}
                {Object.keys(event.config).length > 1 ? ' and more' : ''}.
              </>
            )}
            {event.board &&
              ` Scores on ${data.boards.find((b) => b.id === event.board?.from)?.name ?? event.board.from} also count on its own board.`}
          </Notice>
        );
      })}
      {!onNow.length && soonest?.next && (
        <Notice tone="info" title={`Next: ${soonest.name}`}>
          Turns on for {env === 'test' ? 'testers' : 'players'} on {atDay(soonest.next.start)} UTC,
          and off on {atDay(soonest.next.end)} UTC.
        </Notice>
      )}

      <Panel
        title="Next two weeks"
        description="Events show as bars, rules as marks when they run. Times are UTC."
        actions={<TimelineKey />}
        flush
      >
        <ScheduleTimeline schedule={data} />
      </Panel>

      <Panel
        title="Events"
        description="Config values that switch on and off by themselves, like double points every weekend. An event can have a leaderboard of its own."
        actions={
          <>
            {env === 'test' && (data.events.length > 0 || data.rules.length > 0) && (
              <Button icon={Upload} onClick={() => setCopying(null)}>
                Copy to live…
              </Button>
            )}
            <Button
              variant="primary"
              icon={Plus}
              onClick={() => go({ event: 'new' })}
              disabled={data.events.length >= data.limits.events}
            >
              New event
            </Button>
          </>
        }
        footer={
          data.events.length ? (
            <>
              <span>
                {data.events.length} of {data.limits.events} events
              </span>
              <span>An event turns on and off within a minute of its time.</span>
            </>
          ) : undefined
        }
        flush
      >
        {data.events.length ? (
          <div className="mz-table-wrap">
            <table className="mz-table">
              <caption className="sr-only">Events</caption>
              <thead>
                <tr>
                  <th scope="col" style={{ width: 44 }}>
                    <PickBox
                      label="Select all events"
                      checked={allEvents}
                      onChange={() =>
                        setPicked((now) => ({
                          ...now,
                          events: allEvents ? [] : data.events.map((e) => e.id),
                        }))
                      }
                    />
                  </th>
                  <th scope="col">Event</th>
                  <th scope="col">When</th>
                  <th scope="col">While it’s on</th>
                  <th scope="col">Status</th>
                  <th scope="col">Next</th>
                  <th scope="col" style={{ width: 44 }}>
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.events.map((event) => {
                  const when = whenOf(event);
                  const [key, value] = Object.entries(event.config)[0] ?? [];
                  const more = Object.keys(event.config).length - 1;
                  const status = STATUS[event.status];
                  return (
                    <tr
                      key={event.id}
                      className={`mz-clickable ${isPicked.event(event.id) ? 'bg-cream' : ''}`}
                      onClick={() => go({ event: event.id })}
                    >
                      <PickCell>
                        <PickBox
                          label={`Select ${event.name}`}
                          checked={isPicked.event(event.id)}
                          onChange={() => pick.event(event.id)}
                        />
                      </PickCell>
                      <td>
                        <div className="mz-cell-main flex items-center gap-2">
                          {event.name}
                          {touchedByCli(event.origin) && <CliTag />}
                        </div>
                        <div className="mz-cell-sub mz-mono">{event.id}</div>
                      </td>
                      <td>
                        <div className="mz-cell-main">{when.main}</div>
                        <div className="mz-cell-sub">{when.sub}</div>
                      </td>
                      <td>
                        <div>
                          {key === undefined ? (
                            <span className="text-muted">No config values</span>
                          ) : (
                            <>
                              <code className="mz-code">{key}</code>{' '}
                              <span className="text-muted">
                                {key in data.config ? `${shown(data.config[key])} → ` : ''}
                              </span>
                              <span className="font-medium">{shown(value)}</span>
                              {more > 0 && <span className="text-muted"> and {more} more</span>}
                            </>
                          )}
                        </div>
                        <div className="mz-cell-sub">
                          {event.board
                            ? `Own leaderboard, from ${data.boards.find((b) => b.id === event.board?.from)?.name ?? event.board.from}`
                            : 'No leaderboard'}
                        </div>
                      </td>
                      <td>
                        <Badge tone={status.tone} dot={status.dot}>
                          {event.run?.test ? 'On for testers' : status.label}
                        </Badge>
                      </td>
                      <td className="whitespace-nowrap">{nextOf(event)}</td>
                      <td>
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={Ellipsis}
                          label={`Open ${event.name}`}
                          onClick={() => go({ event: event.id })}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            icon={Clock}
            title="No events yet"
            action={
              <Button variant="primary" size="lg" icon={Plus} onClick={() => go({ event: 'new' })}>
                New event
              </Button>
            }
          >
            An event changes config values for a set time, like double points every weekend.
            {env === 'test'
              ? ' Your test community gets a new event right away.'
              : ' Make them in Test and copy them here, or here directly.'}
          </EmptyState>
        )}
      </Panel>

      <Panel
        title="Rules"
        description="Things MOGZO does by itself: when a leaderboard starts fresh, when an event ends, or at set times."
        actions={
          <Button
            icon={Plus}
            onClick={() => go({ rule: 'new' })}
            disabled={data.rules.length >= data.limits.rules}
          >
            New rule
          </Button>
        }
        flush
      >
        {data.rules.length ? (
          <div className="mz-table-wrap">
            <table className="mz-table">
              <caption className="sr-only">Rules</caption>
              <thead>
                <tr>
                  <th scope="col" style={{ width: 44 }}>
                    <PickBox
                      label="Select all rules"
                      checked={allRules}
                      onChange={() =>
                        setPicked((now) => ({
                          ...now,
                          rules: allRules ? [] : data.rules.map((r) => r.id),
                        }))
                      }
                    />
                  </th>
                  <th scope="col">When</th>
                  <th scope="col">Do</th>
                  <th scope="col">Last run</th>
                  <th scope="col">Next run</th>
                  <th scope="col">On</th>
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.rules.map((rule) => {
                  const when = ruleWhen(rule, data);
                  const does = ruleDoes(rule, data.achievements);
                  return (
                    <tr key={rule.id} className={isPicked.rule(rule.id) ? 'bg-cream' : ''}>
                      <PickCell>
                        <PickBox
                          label={`Select the rule: ${when.main}`}
                          checked={isPicked.rule(rule.id)}
                          onChange={() => pick.rule(rule.id)}
                        />
                      </PickCell>
                      <td>
                        <button
                          type="button"
                          className="mz-cell-main border-0 bg-transparent p-0 text-left font-medium underline-offset-4 hover:underline"
                          onClick={() => go({ rule: String(rule.id) })}
                        >
                          {when.main}
                        </button>
                        <div className="mz-cell-sub">{when.sub}</div>
                      </td>
                      <td>
                        <div className="mz-cell-main flex flex-wrap items-center gap-1">
                          {does.lead} {does.key && <code className="mz-code">{does.key}</code>}{' '}
                          {does.tail}
                          {touchedByCli(rule.origin) && <CliTag />}
                        </div>
                        <div className="mz-cell-sub">{does.sub}</div>
                      </td>
                      <td>
                        {rule.last ? (
                          <>
                            <div className="whitespace-nowrap">{at(rule.last.at)}</div>
                            <div
                              className={`mz-cell-sub ${rule.last.status === 'failed' ? 'text-danger' : ''}`}
                            >
                              {rule.last.message}
                            </div>
                          </>
                        ) : (
                          <span className="text-muted">Not yet</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap">{rule.next ? at(rule.next) : '—'}</td>
                      <td>
                        <Switch
                          label={<span className="sr-only">On</span>}
                          checked={rule.enabled}
                          disabled={busyRule === rule.id}
                          onChange={(change) => toggle(rule, change.target.checked)}
                        />
                      </td>
                      <td className="text-right">
                        {env === 'test' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => runNow(rule)}
                            loading={busyRule === rule.id}
                          >
                            Run now
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            icon={Gauge}
            title="No rules yet"
            action={
              <Button size="lg" icon={Plus} onClick={() => go({ rule: 'new' })}>
                New rule
              </Button>
            }
          >
            A rule runs by itself: give the weekly top 10 an achievement, or set a new daily seed
            every day at 00:00 UTC.
          </EmptyState>
        )}
      </Panel>

      <div className="flex flex-wrap items-start gap-6">
        <Panel
          className="min-w-0 flex-[999_1_520px]"
          title="Recent runs"
          description="Everything the schedule did, newest first. Kept 30 days."
          flush
        >
          {data.runs.length ? (
            <div className="mz-table-wrap">
              <table className="mz-table">
                <caption className="sr-only">Recent runs</caption>
                <thead>
                  <tr>
                    <th scope="col">Time</th>
                    <th scope="col">What happened</th>
                    <th scope="col">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {data.runs.map((run) => (
                    <tr key={`${run.kind}:${run.sourceId}:${run.at}`}>
                      <td className="whitespace-nowrap text-muted">{at(run.at)}</td>
                      <td>
                        <div>{run.message || 'Running…'}</div>
                        <div className="mz-cell-sub">{fromOf(run.kind, run.sourceId, data)}</div>
                      </td>
                      <td>
                        <Badge tone={run.status === 'done' ? 'success' : 'danger'}>
                          {run.status === 'done' ? 'Done' : 'Failed'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="m-0 px-5 py-6 text-muted">
              Nothing has run yet. Runs show here as events turn on and off and rules run.
            </p>
          )}
        </Panel>

        <Panel className="max-w-[400px] min-w-0 flex-[1_1_300px]" title="In your game">
          <div className="flex flex-col gap-3">
            <pre className="m-0 rounded-[6px] border border-line bg-field px-3.5 py-3 font-mono text-[12.5px] leading-5 whitespace-pre-wrap">
              {`// Config already has the values of\n// the events that are on.\nconst config = await mogzo.config.get();\n\n// Which events are on, for a banner\n// or a countdown.\nconst events = await mogzo.events.active();\n\n// While playing, when one turns\n// on or off.\nmogzo.events.onChange((events) => {\n  // show or hide the banner\n});`}
            </pre>
            <ul className="m-0 flex list-disc flex-col gap-2 pl-5 text-muted">
              <li>
                Events change only the values they list. When one ends, those go back to your
                config’s own.
              </li>
              <li>
                Or from the game’s folder: <span className="font-mono">mogzo events list</span>
              </li>
              <li>Rules that post, like a weekly recap, come with game posts.</li>
            </ul>
            <TextLink to="/docs/live-events">Live events in the docs</TextLink>
          </div>
        </Panel>
      </div>

      {pickedCount > 0 && (
        <SelectionBar>
          <strong className="mr-auto font-semibold">
            {countOf(picked.events.length, picked.rules.length)} selected
          </strong>
          <Button size="sm" onClick={() => bulk('on')} disabled={bulkBusy}>
            Turn on
          </Button>
          <Button size="sm" onClick={() => bulk('off')} disabled={bulkBusy}>
            Turn off
          </Button>
          {env === 'test' && (
            <Button size="sm" icon={Upload} onClick={() => setCopying(picked)} disabled={bulkBusy}>
              Copy to live…
            </Button>
          )}
          <Button
            size="sm"
            variant="danger"
            icon={Trash2}
            onClick={() => bulk('trash')}
            loading={bulkBusy}
          >
            Delete
          </Button>
          <Button
            size="sm"
            variant="ghost"
            icon={X}
            label="Clear selection"
            onClick={() => setPicked({ events: [], rules: [] })}
          />
        </SelectionBar>
      )}

      {copying !== false && (
        <CopyToLive
          projectId={project.id}
          schedule={data}
          picks={copying}
          onClose={() => setCopying(false)}
          onCopied={(result) => {
            setCopying(false);
            setPicked({ events: [], rules: [] });
            setMoved(null);
            setMessage(copiedText(result));
            refresh();
          }}
        />
      )}
    </>
  );
}

/** A row's checkbox cell: ticking it doesn't open the row. */
function PickCell({ children }: { children: ReactNode }) {
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: only stops the row's click; the checkbox has its own keys
    <td onClick={(event) => event.stopPropagation()}>{children}</td>
  );
}

function PickBox({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <Checkbox
      label={<span className="sr-only">{label}</span>}
      checked={checked}
      onChange={onChange}
    />
  );
}
